import type { BillingPlanIntentDesiredPlanV1 } from "@intelligems/ig-types/dtos/billing/commands/plan-intent";
import type { LivePriceDto } from "@intelligems/ig-types/dtos/live-prices";
import { getDesiredProductSetTarget } from "@src/utils/billing-plan-intent";

import type { BillingSelectionIntent } from "../types";
import { matchLivePriceByIds } from "./catalog";
import {
  SMART_CONTENT_ID,
  SMART_PRICING_UNLIMITED_ID,
  SMART_PRICING_VARIANTS,
  UNLIMITED_ID,
} from "./constants";
import { getEntityCadencePrice, getSelectionTotals } from "./pricing";
import type {
  BillingCatalog,
  BillingCycle,
  Selection,
  SelectionState,
} from "./types";

type InitialSelectionOptions = Readonly<{
  preselectedModuleIds?: readonly string[];
  preselectedPlanId?: string | null;
  reactivatingEntityId?: string | null;
}>;

type DesiredPlanCadenceCatalogs = Readonly<
  Record<BillingCycle, BillingCatalog>
>;

const normalizeModuleIds = (
  moduleIds: readonly string[],
  catalog: BillingCatalog
): string[] => {
  const availableIds = new Set(catalog.modules.map(({ id }) => id));
  const selectedIds = new Set(
    moduleIds.filter(
      (id) => availableIds.has(id) && !SMART_PRICING_VARIANTS.has(id)
    )
  );
  const smartPricingVariantId = [...moduleIds]
    .reverse()
    .find((id) => availableIds.has(id) && SMART_PRICING_VARIANTS.has(id));

  if (smartPricingVariantId) {
    selectedIds.add(smartPricingVariantId);
  }
  if (selectedIds.size > 0 && availableIds.has(SMART_CONTENT_ID)) {
    selectedIds.add(SMART_CONTENT_ID);
  }

  return catalog.modules
    .filter(({ id }) => selectedIds.has(id))
    .map(({ id }) => id);
};

const completeModuleIds = (
  moduleIds: readonly string[],
  catalog: BillingCatalog
): string[] => {
  // The Unlimited plan always carries the Unlimited Smart Pricing variant —
  // it supersedes a previously selected Limited variant.
  const variantId = catalog.modules.some(
    ({ id }) => id === SMART_PRICING_UNLIMITED_ID
  )
    ? SMART_PRICING_UNLIMITED_ID
    : moduleIds.find((id) => SMART_PRICING_VARIANTS.has(id));

  return normalizeModuleIds(
    catalog.modules
      .filter(({ id }) => !SMART_PRICING_VARIANTS.has(id) || id === variantId)
      .map(({ id }) => id),
    catalog
  );
};

const hasUnlimitedPlan = (catalog: BillingCatalog): boolean =>
  catalog.plans.some(({ id }) => id === UNLIMITED_ID);

export const isUnlimitedEligible = (
  moduleIds: readonly string[],
  catalog: BillingCatalog
): boolean => {
  const unlimited = catalog.plans.find(({ id }) => id === UNLIMITED_ID);
  if (!unlimited || moduleIds.length === 0) {
    return false;
  }

  const { selectionSum } = getSelectionTotals(
    { plans: catalog.plans, modules: catalog.modules },
    [...moduleIds],
    catalog.cadence
  );
  const unlimitedPrice = getEntityCadencePrice(unlimited, catalog.cadence);

  return selectionSum >= unlimitedPrice;
};

const normalizeSelection = ({
  catalog,
  moduleIds,
  planId,
  reactivatingEntityId,
}: {
  catalog: BillingCatalog;
  moduleIds: readonly string[];
  planId?: string;
  reactivatingEntityId: string | null;
}): SelectionState => {
  let normalizedModuleIds = normalizeModuleIds(moduleIds, catalog);
  const selectUnlimited = hasUnlimitedPlan(catalog) && planId === UNLIMITED_ID;

  if (selectUnlimited) {
    normalizedModuleIds = completeModuleIds(normalizedModuleIds, catalog);
  }

  return {
    cadence: catalog.cadence,
    moduleIds: normalizedModuleIds,
    planId: selectUnlimited ? UNLIMITED_ID : undefined,
    reactivatingEntityId,
  };
};

export const createInitialSelection = (
  catalog: BillingCatalog,
  options: InitialSelectionOptions = {}
): SelectionState => {
  const activeModuleIds = catalog.activeModules
    .filter(
      ({ trialEndAt, trialConverts }) =>
        trialEndAt === undefined || trialConverts
    )
    .map(({ id }) => id);

  return normalizeSelection({
    catalog,
    moduleIds: options.preselectedModuleIds
      ? [...activeModuleIds, ...options.preselectedModuleIds]
      : activeModuleIds,
    planId:
      options.preselectedPlanId === undefined
        ? catalog.currentPlan?.id
        : (options.preselectedPlanId ?? undefined),
    reactivatingEntityId: options.reactivatingEntityId ?? null,
  });
};

const cadenceMatchCount = (
  catalog: BillingCatalog,
  targetPriceIds: ReadonlySet<string>
): number =>
  [...catalog.plans, ...catalog.modules].filter(
    ({ stripePriceId }) =>
      stripePriceId != null && targetPriceIds.has(stripePriceId)
  ).length;

export const desiredPlanCadence = (
  desiredPlan?: BillingPlanIntentDesiredPlanV1,
  catalogs?: DesiredPlanCadenceCatalogs,
  livePrices: LivePriceDto[] = []
): BillingCycle | undefined => {
  if (!desiredPlan) {
    return undefined;
  }

  const { intentPayload } = desiredPlan.normalizedIntent;
  if ("requestedCadence" in intentPayload) {
    return intentPayload.requestedCadence === "annual" ? "year" : "month";
  }

  const target = getDesiredProductSetTarget(desiredPlan);
  if (!target || !catalogs) {
    return undefined;
  }

  const targetPriceIds = new Set(
    target.priceIds.map(
      (priceId) => matchLivePriceByIds([priceId], livePrices)?.id ?? priceId
    )
  );
  const monthlyMatches = cadenceMatchCount(catalogs.month, targetPriceIds);
  const annualMatches = cadenceMatchCount(catalogs.year, targetPriceIds);

  if (annualMatches > monthlyMatches) {
    return "year";
  }
  if (monthlyMatches > annualMatches) {
    return "month";
  }
  return undefined;
};

export const createInitialSelectionFromDesiredPlan = (
  catalog: BillingCatalog,
  desiredPlan?: BillingPlanIntentDesiredPlanV1,
  options: InitialSelectionOptions = {},
  livePrices: LivePriceDto[] = []
): SelectionState => {
  if (!desiredPlan) {
    return createInitialSelection(catalog, options);
  }

  const target = getDesiredProductSetTarget(desiredPlan);
  if (!target) {
    return createInitialSelection(catalog, options);
  }

  const targetPriceIds = new Set(
    target.priceIds.map(
      (priceId) =>
        matchLivePriceByIds([priceId], livePrices, catalog.cadence)?.id ??
        priceId
    )
  );
  const planId = catalog.plans.find(
    ({ stripePriceId }) =>
      stripePriceId != null && targetPriceIds.has(stripePriceId)
  )?.id;
  // A trialing price that cancels is pinned into every target regardless of
  // choice, so seeding it would falsely confirm the trial on submit; a
  // converting trial is already committed to the plan, so it seeds like any
  // other active module.
  const moduleIds = catalog.modules
    .filter(
      ({ stripePriceId, trialEndAt, trialConverts }) =>
        stripePriceId != null &&
        targetPriceIds.has(stripePriceId) &&
        (trialEndAt === undefined || trialConverts)
    )
    .map(({ id }) => id);

  if (!planId && moduleIds.length === 0) {
    return createInitialSelection(catalog, options);
  }

  return normalizeSelection({
    catalog,
    moduleIds: options.preselectedModuleIds
      ? [...moduleIds, ...options.preselectedModuleIds]
      : moduleIds,
    planId:
      options.preselectedPlanId === undefined
        ? planId
        : (options.preselectedPlanId ?? undefined),
    reactivatingEntityId: options.reactivatingEntityId ?? null,
  });
};

export const transitionSelection = (
  selection: SelectionState,
  intent: BillingSelectionIntent,
  catalog: BillingCatalog
): SelectionState => {
  if (intent.type === "change_cadence") {
    const expectedCatalogCadence =
      intent.cadence === "annual" ? "year" : "month";
    if (catalog.cadence !== expectedCatalogCadence) {
      throw new Error(
        `${intent.cadence} billing cadence requires a ${expectedCatalogCadence} catalog`
      );
    }

    return normalizeSelection({
      catalog,
      moduleIds: selection.moduleIds,
      planId: selection.planId,
      reactivatingEntityId: selection.reactivatingEntityId,
    });
  }

  if (intent.type === "reactivate") {
    const selected = transitionSelection(
      selection,
      { type: "select", entityId: intent.entityId },
      catalog
    );

    return { ...selected, reactivatingEntityId: intent.entityId };
  }

  if (intent.type === "select") {
    if (intent.entityId === UNLIMITED_ID) {
      return normalizeSelection({
        catalog,
        moduleIds: completeModuleIds(selection.moduleIds, catalog),
        planId: UNLIMITED_ID,
        reactivatingEntityId: selection.reactivatingEntityId,
      });
    }

    const moduleIds = SMART_PRICING_VARIANTS.has(intent.entityId)
      ? [
          ...selection.moduleIds.filter(
            (id) => !SMART_PRICING_VARIANTS.has(id)
          ),
          intent.entityId,
        ]
      : [...selection.moduleIds, intent.entityId];

    return normalizeSelection({
      catalog,
      moduleIds,
      planId: selection.planId,
      reactivatingEntityId: selection.reactivatingEntityId,
    });
  }

  const deselectsUnlimited = intent.entityId === UNLIMITED_ID;
  return normalizeSelection({
    catalog,
    moduleIds: deselectsUnlimited
      ? selection.moduleIds
      : selection.moduleIds.filter((id) => id !== intent.entityId),
    planId:
      deselectsUnlimited || selection.planId === UNLIMITED_ID
        ? undefined
        : selection.planId,
    reactivatingEntityId: selection.reactivatingEntityId,
  });
};

export const isSelectionNoOp = (
  selection: SelectionState,
  currentSelection: Selection
): boolean => {
  if (selection.reactivatingEntityId !== null) {
    return false;
  }

  const selectedModuleIds = new Set(selection.moduleIds);
  const currentModuleIds = new Set(currentSelection.moduleIds);

  return (
    selection.planId === currentSelection.planId &&
    selectedModuleIds.size === currentModuleIds.size &&
    [...selectedModuleIds].every((id) => currentModuleIds.has(id))
  );
};
